import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import './VolunteerDashboard.css'
import '../styles/dashboard-header.css'
import LoadingScreen from '../components/LoadingScreen'

const VolunteerDashboard = () => {
  const [user, setUser] = useState(null)
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const history = useHistory()

  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (!token) {
      history.push('/login')
      return
    }

    const userData = JSON.parse(localStorage.getItem('user') || '{}')
    if (userData.role !== 'volunteer') {
      // Redirect to appropriate dashboard
      if (userData.role === 'admin') {
        history.push('/admin-dashboard')
      } else if (userData.role === 'teacher') {
        history.push('/teacher-dashboard')
      } else if (userData.role === 'invigilator') {
        history.push('/invigilator-dashboard')
      } else {
        history.push('/student-dashboard')
      }
      return
    }

    setUser(userData)
    fetchDashboardData()
  }, [history])

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch('/api/volunteer/dashboard', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        
        // Check if pending approval
        if (data.pending_approval) {
          setError(data.message || 'Your volunteer account is pending admin approval.')
          setDashboardData(data)
        } else {
          setDashboardData(data)
        }
      } else {
        const errorData = await response.json()
        setError(errorData.error || 'Failed to load dashboard data')
      }
    } catch (err) {
      setError('Network error')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user')
    history.push('/login')
  }

  if (loading) {
    return <LoadingScreen message="Loading Volunteer Dashboard" />
  }

  if (error && !dashboardData?.pending_approval) {
    return (
      <div className="volunteer-dashboard-container">
        <div className="error">{error}</div>
      </div>
    )
  }

  // Show pending approval message
  if (dashboardData?.pending_approval) {
    return (
      <div className="volunteer-dashboard-container">
        <header className="volunteer-header">
          <div className="header-content">
            <h1>Volunteer Dashboard</h1>
            <div className="user-info">
              <span>Welcome, {user?.full_name}</span>
              <button onClick={handleLogout} className="logout-button">
                Logout
              </button>
            </div>
          </div>
        </header>
        <main className="volunteer-main">
          <div className="pending-approval-message">
            <div className="pending-icon">⏳</div>
            <h2>Account Pending Approval</h2>
            <p>{dashboardData.message || 'Your volunteer account is pending admin approval. You will be notified once your account is approved.'}</p>
            <p className="pending-note">Please check back later or contact an administrator.</p>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="volunteer-dashboard-container">
      <header className="volunteer-header">
        <div className="header-content">
          <h1>Volunteer Dashboard</h1>
          <div className="user-info">
            <span>Welcome, {user?.full_name}</span>
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="volunteer-main">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📋</div>
            <div className="stat-content">
              <h3>{dashboardData?.statistics?.total_tasks || 0}</h3>
              <p>Total Tasks</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <h3>{dashboardData?.statistics?.completed_tasks || 0}</h3>
              <p>Completed Tasks</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">⏳</div>
            <div className="stat-content">
              <h3>{dashboardData?.statistics?.pending_tasks || 0}</h3>
              <p>Pending Tasks</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">📝</div>
            <div className="stat-content">
              <h3>{dashboardData?.statistics?.available_tests_count || 0}</h3>
              <p>Available Tests</p>
            </div>
          </div>
        </div>

        <div className="volunteer-sections">
          <section className="available-tests">
            <h2>Available Tests</h2>
            <div className="tests-list">
              {dashboardData?.available_tests?.length > 0 ? (
                dashboardData.available_tests.map((test) => (
                  <div key={test.id} className="volunteer-test-item">
                    <div className="volunteer-test-info">
                      <h4>{test.title}</h4>
                      <p>{test.description || 'No description'}</p>
                      <p>Attempts: {test.total_attempts || 0}</p>
                    </div>
                    <div className="volunteer-test-actions">
                      <button 
                        className="volunteer-view-button"
                        onClick={() => history.push(`/volunteer/test/${test.id}`)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p>No available tests</p>
              )}
            </div>
          </section>

          <section className="assigned-tasks">
            <h2>Assigned Tasks</h2>
            <div className="tasks-list">
              {dashboardData?.assigned_tasks?.length > 0 ? (
                dashboardData.assigned_tasks.map((task) => (
                  <div key={task.id} className="volunteer-task-item">
                    <div className="volunteer-task-info">
                      <h4>{task.task_type || 'General Task'}</h4>
                      <p>{task.task_description || 'No description'}</p>
                      <p>Status: <span className={`status-badge status-${task.status}`}>{task.status}</span></p>
                      {task.related_test_title && (
                        <p>Related Test: {task.related_test_title}</p>
                      )}
                    </div>
                    <div className="task-date">
                      {new Date(task.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ))
              ) : (
                <p>No assigned tasks</p>
              )}
            </div>
          </section>
        </div>

        <div className="activity-log-section">
          <h2>Recent Activity</h2>
          <div className="activity-list">
            {dashboardData?.activity_log?.length > 0 ? (
              dashboardData.activity_log.map((activity, index) => (
                <div key={index} className="activity-item">
                  <div className="activity-icon">📌</div>
                  <div className="activity-content">
                    <p>{activity.description || activity.type}</p>
                    <span className="activity-time">
                      {new Date(activity.timestamp).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p>No recent activity</p>
            )}
          </div>
        </div>

        <div className="action-buttons">
          <button
            className="primary-button"
            onClick={() => history.push('/volunteer/tests')}
          >
            View All Tests
          </button>
          <button
            className="secondary-button"
            onClick={() => history.push('/volunteer/tasks')}
          >
            View My Tasks
          </button>
          <button
            className="secondary-button"
            onClick={() => history.push('/volunteer/analytics')}
          >
            View Analytics
          </button>
        </div>
      </main>
    </div>
  )
}

export default VolunteerDashboard

